import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { useAsync } from 'react-use';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import AppService from 'src/services/AppService';
import { ArrayUtils } from 'src/utils';
import { trackingPage } from 'src/utils/tracking';
import {
    isIosSelector,
    isMiniappSelector,
    isZaloSeletor,
} from 'src/selectors/app';
import { isLoggedSelector, strUserIdSelector } from 'src/selectors/auth';
import { Element, Image, RouterLink, Section, Title } from 'src/components';
import Card from 'src/components/card';
import { TruncateText } from 'src/modules/shared/components/Truncate';
import ContentDescription from 'src/modules/content/components/shared/ContentDescription';
import { useTracking } from './useTracking';

interface IProps {
    title: string;
    size: number;
    categoryId: number;
}
interface IZShortVideo {
    id: number;
    url: string;
    thumb: string;
    dynamicThumbUrl: string;
    thumbDynamicDuration: number;
    description: string;
    zaloIosScheme: string;
    zaloAndroidScheme: string;
    source: string;
    extra: string;
    author?: string;
    authorAvatar?: string;
}

export function ZShortSection(props: any) {
    const isZalo = useSelector(isZaloSeletor);
    const isLogin = useSelector(isLoggedSelector);

    if (!isZalo && !isLogin) {
        return <Element className={cn(props.classNameWrapper)} />;
    }
    
    return (
        <Section className={cn(props.classNameWrapper)}>
            <Element
                className={cn(
                    props.className,
                    '[font-family:var(--font-family-sans-serif)] empty:hidden',
                )}
            >
                <ZShortItems {...props} />
            </Element>
        </Section>
    );
}

function ZShortItems({ title = 'Zalo Video', size, categoryId }: IProps) {
    const [currentPlay, setCurrentPlay] = useState<number>(-1);
    const [listInView, setListInView] = useState<number[]>([]);

    const state = useAsync(() => AppService.getZShorts(size, categoryId));
    const timeout = useRef<any>(0);
    const data = state.value?.data || {};
    const { items = [], ids = [] } = data;

    useEffect(() => {
        if (!ArrayUtils.isEmpty(listInView)) {
            if (currentPlay === -1) {
                setCurrentPlay(listInView[0]);
            }
        } else {
            setCurrentPlay(-1);
        }
    }, [listInView, currentPlay]);

    useEffect(() => {
        if (!ArrayUtils.isEmpty(listInView)) {
            setCurrentPlay(listInView[0]);
        }
    }, [listInView]);

    const renderItem = (item: IZShortVideo, index: number) => {
        return (
            <React.Fragment key={index}>
                <ZShortItem
                    item={item}
                    ids={ids}
                    listInView={listInView}
                    isPlaying={currentPlay === item.id}
                    setCurrentPlay={setCurrentPlay}
                    setListInView={setListInView}
                    timeout={timeout}
                />
            </React.Fragment>
        );
    };

    if (items.length < 3) {
        return null;
    }

    return (
        <>
            <Element
                className={cn(
                    'flex items-center',
                    'text-[#747474]',
                    'mb-[14px]',
                    'text-[14px]',
                )}
            >
                <Element
                    className={cn(
                        'w-[20px] h-[20px]',
                        'mr-[8px]',
                        'bg-cover bg-center',
                        'bg-no-repeat',
                        'bg-[url(https://baomoi-static.bmcdn.me/wap/styles/img/event/zshort/logo.svg)]',
                    )}
                />
                <Title>{title}</Title>
                <Element
                    className={cn(
                        'mx-[10px]',
                        'w-[3px] h-[3px]',
                        'bg-[#808080]',
                        'rounded-full',
                    )}
                ></Element>
                <Title renderAs="h4" className={cn('text-[#1E2127]')}>
                    Gợi ý cho bạn
                </Title>
            </Element>
            <Element className="ml-[-20px] w-screen">
                <Element className="flex items-center overflow-auto [&::-webkit-scrollbar]:hidden px-[16px]">
                    {items.map((item: IZShortVideo, index: number) => {
                        return renderItem(item, index);
                    })}
                </Element>
            </Element>
        </>
    );
}

const ZShortItem = ({
    item,
    ids,
    setCurrentPlay,
    listInView = [],
    setListInView,
    isPlaying = false,
    timeout,
}: {
    item: IZShortVideo;
    ids: number[];
    setCurrentPlay: any;
    listInView: number[];
    setListInView: any;
    isPlaying: boolean;
    timeout?: any;
}) => {
    const isIOS = useSelector(isIosSelector);
    const isZalo = useSelector(isZaloSeletor);
    const strUserId = useSelector(strUserIdSelector);
    const isIframe = useSelector(isMiniappSelector);

    const url = isIOS
        ? item.zaloIosScheme + '?source=' + item.source
        : item.zaloAndroidScheme + '?source=' + item.source;
    const [trackingRef, inView] = useTracking(item.id, item.source, item.extra);

    useEffect(() => {
        if (inView) {
            if (listInView.indexOf(item.id) > -1) {
                return;
            }
            const index = ids.indexOf(item.id);
            setListInView((listInView: number[]) => {
                return index > listInView.length
                    ? [...listInView, item.id]
                    : [
                          ...listInView.slice(0, index),
                          item.id,
                          ...listInView.slice(index),
                      ];
            });
        } else {
            if (listInView.indexOf(item.id) > -1) {
                setListInView((listInView: number[]) =>
                    ArrayUtils.remove([...listInView], item.id),
                );
            }
        }
    }, [inView]);

    useEffect(() => {
        if (isPlaying && !ArrayUtils.isEmpty(listInView)) {
            timeout.current = setTimeout(() => {
                const nextItem = getNextIndex(item.id);
                setCurrentPlay(nextItem);
            }, item.thumbDynamicDuration * 2);
        }
        return () => {
            clearTimeout(timeout.current);
        };
    }, [isPlaying]);

    const getNextIndex = (id: number) => {
        const cIndex = listInView.indexOf(id);
        if (listInView[cIndex + 1]) {
            return listInView[cIndex + 1];
        }
        return listInView[0];
    };

    const handleClick = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();
        trackingPage(
            'mobile_zshort',
            item.id,
            strUserId,
            item.source,
            item.extra,
        );
        if (isIframe) {
            window.parent.postMessage(
                {
                    type: 'OPEN_LINK_FROM_BAOMOI',
                    url: item.url + '?source=' + item.source,
                },
                'https://h5.zdn.vn',
            );
        } else {
            window.open(url, '_blank');
        }
    };

    const objEllipsis = useMemo(() => {
        return {
            widthContainer: 138,
            sizeText: '16px',
            fontWeight: '600',
        };
    }, []);

    const renderItems = useCallback(() => {
        return (
            <Card
                className={cn(
                    'w-[154px] mx-[4px]',
                    'shrink-0',
                    'pointer-events-none',
                )}
                domRef={trackingRef}
            >
                <Card.Image className={cn('h-[274px] rounded-[8px]')}>
                    <Image
                        className={cn(
                            'before:pointer-events-none',
                            'before:absolute before:z-[1] before:w-full before:h-[85px] before:bottom-0 before:left-0',
                            'before:bg-gradient-to-b before:from-[transparent] before:to-[rgba(25,25,25,0.8)]',

                            'after:pointer-events-none',
                            'after:absolute after:z-[1] after:w-full after:h-full after:bottom-0 after:left-0',
                            'after:bg-gradient-to-b after:from-[rgba(0,0,0,0.2)] from-[0%] after:via-[transparent] via-50%  after:to-[rgba(0,0,0,0.4)] to-[100%]',
                        )}
                        src={item.thumb}
                        alt={item.description}
                    />
                    <Image
                        className={twMerge(
                            'absolute top-0 bottom-0',
                            'opacity-0',
                            cn({
                                ['opacity-100']: isPlaying,
                            }),
                        )}
                        src={item.dynamicThumbUrl}
                        alt={item.description}
                    />
                </Card.Image>
                {/* <Card.Content className="absolute z-[1] right-[8px] left-[8px] bottom-[8px] text-white">
                    <Card.Header>
                        <ContentDescription
                            objEllipsis={objEllipsis}
                            className="line-clamp-2 [display:-webkit-box] text-inherit font-semibold text-[1.6rem] relative"
                            description={item.description}
                        />
                        {item.authorAvatar && item.author && (
                            <Element className="flex items-center mt-[4px] leading-[normal]">
                                <Image
                                    src={item.authorAvatar}
                                    className="w-[14px] h-[14px] rounded-full overflow-hidden border-[0.5px] border-solid border-white shrink-0"
                                />
                                <Element className="text-[1.2rem] ml-[4px]">
                                    <TruncateText
                                        content={item.author}
                                        objEllipsis={{
                                            line: 1,
                                            widthContainer: 120,
                                            sizeText: '12px',
                                        }}
                                    />
                                </Element>
                            </Element>
                        )}
                    </Card.Header>
                </Card.Content> */}
            </Card>
        );
    }, [item, isPlaying]);

    return (
        <>
            {isZalo ? (
                <RouterLink to={url} newTab onClick={handleClick}>
                    {renderItems()}
                </RouterLink>
            ) : (
                <Element onClick={handleClick}> {renderItems()}</Element>
            )}
        </>
    );
};

export default memo(ZShortSection);
